import React, { useEffect, useState } from 'react';
import { cn } from './cn';

export type TabCountTone = 'neutral' | 'brand' | 'warning' | 'danger';

export interface TabItem {
  id: string;
  label: React.ReactNode;
  count?: number;
  /** Visual emphasis for the trailing count pill. Defaults to 'neutral'. */
  countTone?: TabCountTone;
}

const COUNT_PILL: Record<TabCountTone, string> = {
  neutral: 'bg-surface-2 text-ink-muted',
  brand:   'bg-brand-soft text-brand',
  warning: 'bg-sev-minor-soft text-sev-minor-ink',
  danger:  'bg-sev-critical-soft text-sev-critical-ink',
};

export interface TabsProps {
  items: TabItem[];
  activeId: string;
  onChange: (id: string) => void;
  variant?: 'underline' | 'pills' | 'segmented';
  className?: string;
  /** ARIA label for the tab list. */
  label?: string;
  /**
   * Pills variant only. When false, tabs stay on a single row and overflow
   * is delegated to the parent (typically wrapped in `overflow-x-auto`).
   * Default true keeps the existing wrap-to-multiple-rows behaviour.
   */
  wrap?: boolean;
  /**
   * Pills variant only. When set, render only the first N pills initially
   * with a trailing "+ X more" toggle that expands to show the rest.
   * Auto-expands when the active tab is in the hidden set so the user
   * never lands on a tab that isn't visible.
   */
  collapsedCount?: number;
}

export const Tabs: React.FC<TabsProps> = ({
  items, activeId, onChange, variant = 'underline', className, label, wrap = true,
  collapsedCount,
}) => {
  // Hooks must run on every render regardless of variant.
  const [expanded, setExpanded] = useState(false);
  const showCollapse =
    variant === 'pills' &&
    typeof collapsedCount === 'number' &&
    items.length > collapsedCount;
  const activeIndex = items.findIndex((i) => i.id === activeId);
  const activeIsHidden =
    showCollapse && activeIndex >= 0 && activeIndex >= (collapsedCount as number);

  useEffect(() => {
    if (activeIsHidden) setExpanded(true);
  }, [activeIsHidden]);

  if (variant === 'segmented') {
    return (
      <div
        role="tablist"
        aria-label={label}
        className={cn(
          'inline-flex p-1 bg-surface-2 rounded-lg border border-border-subtle',
          className,
        )}
      >
        {items.map(item => {
          const isActive = item.id === activeId;
          return (
            <button
              key={item.id}
              role="tab"
              type="button"
              aria-selected={isActive}
              onClick={() => onChange(item.id)}
              className={cn(
                'px-3 h-8 rounded-md text-body-sm font-medium whitespace-nowrap transition-all duration-180 ease-premium',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                isActive
                  ? 'bg-surface-1 text-ink shadow-xs'
                  : 'text-ink-subtle hover:text-ink',
              )}
            >
              {item.label}
              {typeof item.count === 'number' && (
                <span className={cn(
                  'ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] h-4 px-1 rounded-full text-[10.5px] font-semibold tabular-nums',
                  COUNT_PILL[item.countTone ?? 'neutral'],
                )}>{item.count}</span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  if (variant === 'pills') {
    const visibleItems = !showCollapse || expanded
      ? items
      : items.slice(0, collapsedCount as number);
    const hiddenCount = items.length - visibleItems.length;
    return (
      <div
        role="tablist"
        aria-label={label}
        className={cn('flex items-center gap-2', wrap && 'flex-wrap', className)}
      >
        {visibleItems.map(item => {
          const isActive = item.id === activeId;
          const tone = item.countTone ?? 'neutral';
          return (
            <button
              key={item.id}
              role="tab"
              type="button"
              aria-selected={isActive}
              onClick={() => onChange(item.id)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-body-sm font-medium whitespace-nowrap shrink-0 transition-all duration-180 ease-premium',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                isActive
                  ? 'bg-[var(--ink-800)] text-white hover:bg-[var(--ink-700)]'
                  : 'text-ink-subtle hover:text-ink hover:bg-surface-2',
              )}
            >
              {item.label}
              {typeof item.count === 'number' && item.count > 0 && (
                <span className={cn(
                  'inline-flex items-center justify-center min-w-[1.25rem] h-4 px-1 rounded-full text-[10.5px] font-semibold tabular-nums',
                  isActive ? 'bg-white/20 text-white' : COUNT_PILL[tone],
                )}>{item.count}</span>
              )}
            </button>
          );
        })}
        {showCollapse && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className={cn(
              'inline-flex items-center gap-1 px-3 h-8 rounded-full text-body-sm font-medium whitespace-nowrap shrink-0 transition-colors duration-180',
              'border border-dashed border-border text-ink-subtle hover:text-brand hover:border-brand/60',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
            )}
            aria-expanded={expanded}
          >
            {expanded ? 'Show fewer' : `+ ${hiddenCount} more`}
          </button>
        )}
      </div>
    );
  }

  // underline
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn('flex items-center gap-6 border-b border-border-subtle', className)}
    >
      {items.map(item => {
        const isActive = item.id === activeId;
        return (
          <button
            key={item.id}
            role="tab"
            type="button"
            aria-selected={isActive}
            onClick={() => onChange(item.id)}
            className={cn(
              'relative -mb-px py-3 text-body-sm font-medium whitespace-nowrap shrink-0 transition-colors duration-180',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
              isActive ? 'text-ink' : 'text-ink-subtle hover:text-ink',
            )}
          >
            {item.label}
            {typeof item.count === 'number' && item.count > 0 && (
              <span className={cn(
                'ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-4 px-1 rounded-full text-[10.5px] font-semibold tabular-nums',
                COUNT_PILL[item.countTone ?? 'neutral'],
              )}>{item.count}</span>
            )}
            {isActive && (
              <span className="absolute inset-x-0 -bottom-px h-0.5 bg-ink rounded-full" aria-hidden />
            )}
          </button>
        );
      })}
    </div>
  );
};
